// Cross-cutting prompter for the passkey sudo re-auth flow.
//
// Background: backend #1042 (PR #1189) gates passkey add / rename / remove
// behind a 5-minute "sudo" window. When the window expires, the API returns
// 403 with { error: "passkey_sudo_required" }. The SPA must intercept that,
// run a fresh WebAuthn assertion against /api/admin/webauthn/login/*, then
// retry the original request. The mutation handler in the calling component
// never sees the 403.
//
// Why a global prompter and not per-component handling? The 403 can hit
// from any /api/admin/me/passkeys/* call, and we don't want every passkey
// surface (DashboardAccountView today, future surfaces tomorrow) to
// reimplement the modal+retry choreography. The prompter is a single
// pluggable function — apiClient.ts calls it; the app shell mounts a
// modal and registers it as the prompter on mount.
//
// Contract: the prompter returns `true` when the user successfully
// re-asserted (a fresh passkey_sudo cookie is now on the wire), `false`
// when they cancelled or the assertion failed. The caller retries on
// true, surfaces the original 403 on false.

export type PasskeySudoPrompter = () => Promise<boolean>

// Default: no prompter wired. Returns false so the apiClient surfaces the
// 403 as-is (the calling component sees the unhandled error). The app
// shell installs the real prompter on mount; this guard makes apiClient
// safe to import in test contexts and pre-mount code paths.
let prompter: PasskeySudoPrompter = async () => false

export function setPasskeySudoPrompter(p: PasskeySudoPrompter): void {
  prompter = p
}

export function clearPasskeySudoPrompter(): void {
  prompter = async () => false
}

// Single-flight coalescing — two parallel mutations that both 403 should
// open ONE modal, not two stacked ones. Both callers await the same
// promise; both retry on resolve.
let inflight: Promise<boolean> | null = null

export function requestPasskeySudo(): Promise<boolean> {
  if (!inflight) {
    inflight = prompter().finally(() => {
      inflight = null
    })
  }
  return inflight
}
